import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort,
} from "@syncfusion/ej2-react-grids";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { equipmentModelsService } from "../../Services/equipment.models.service";
import { manufacturerService } from "../../Services/manufacturer.service";

const PAGE_TITLE = "Comandos de Modelos de Equipamentos";

export default function EquipmentModelCommands() {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [loadingCommandId, setLoadingCommandId] = useState(null);

  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState("");
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [commands, setCommands] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const calculatePageSize = () => {
    const available = window.innerHeight - 290;
    setPageSize(Math.max(5, Math.floor(available / 30)));
  };

  useLayoutEffect(() => {
    calculatePageSize();
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculatePageSize, 300);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "title", value: PAGE_TITLE });
    loadManufacturers();
    loadEquipmentModels();
    loadCommands();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const loadManufacturers = async () => {
    try {
      const response = await manufacturerService.getById(0);
      setManufacturers(Array.isArray(response) ? response : []);
    } catch {
      setManufacturers([]);
    }
  };

  const loadEquipmentModels = async () => {
    try {
      const response = await equipmentModelsService.fetchEquipmentModels();
      const normalized = Array.isArray(response)
        ? response.filter((m) => m?.equipmentModelId || m?.EquipmentModelId)
        : [];
      setEquipmentModels(normalized);
      setFilteredModels(normalized);
    } catch {
      setEquipmentModels([]);
      setFilteredModels([]);
    }
  };

  const showErrorToast = (error) => {
    const msg = error?.response?.data?.message || "Ocorreu um erro ao processar a requisição.";
    setToastParams(JSON.stringify({ show: true, title: "Erro", body: msg, color: "danger" }));
  };

  const loadCommands = async (modelId) => {
    if (!modelId) {
      setCommands([]);
      return;
    }

    setIsLoading(true);
    try {
      const [allCommandsResponse, linkedCommandsResponse] = await Promise.all([
        equipmentModelsService.fetchEquipmentCommands(),
        equipmentModelsService.fetchEquipmentModelCommands({ EquipmentModelId: modelId }),
      ]);

      const allCommands = Array.isArray(allCommandsResponse)
        ? allCommandsResponse
        : allCommandsResponse
          ? [allCommandsResponse]
          : [];

      const linkedCommands = Array.isArray(linkedCommandsResponse)
        ? linkedCommandsResponse
        : linkedCommandsResponse
          ? [linkedCommandsResponse]
          : [];

      const linkedIds = new Set(
        linkedCommands
          .map((item) => String(item.commandId ?? item.CommandId ?? ""))
          .filter((id) => id !== "")
      );

      const mergedCommands = allCommands.map((item) => {
        const commandId = item.commandId ?? item.CommandId;
        const description = item.commandDescription ?? item.CommandDescription ?? "";

        return {
          ...item,
          commandId,
          CommandId: commandId,
          commandDescription: description,
          CommandDescription: description,
          linked: linkedIds.has(String(commandId)) ? "S" : "N",
        };
      });

      setCommands(mergedCommands);
    } catch (error) {
      showErrorToast(error);
      setCommands([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManufacturerChange = (e) => {
    const mId = e.target.value;
    setSelectedManufacturerId(mId);
    setSelectedModelId("");
    setCommands([]);
    setFilteredModels(
      mId
        ? equipmentModels.filter((m) => String(m.manufacturerId ?? m.ManufacturerId) === mId)
        : equipmentModels
    );
  };

  const handleModelChange = (e) => {
    const id = e.target.value;
    setSelectedModelId(id);
    setCommands([]);
    loadCommands(id);
  };

  const handleCheckboxChange = async (item) => {
    if (!selectedModelId) return;
    const commandId = item.commandId ?? item.CommandId;
    const linkedRaw = item.linked ?? item.Linked;
    const linked = linkedRaw === "S" || linkedRaw === true || linkedRaw === 1;
    
    setLoadingCommandId(commandId);
    try {
      if (linked) {
        await equipmentModelsService.unlinkEquipmentModelCommand(Number(selectedModelId), commandId);
      } else {
        await equipmentModelsService.linkEquipmentModelCommand(Number(selectedModelId), commandId);
      }
      setToastParams(JSON.stringify({
        show: true,
        title: "Sucesso",
        body: linked ? "Comando desvinculado com sucesso." : "Comando vinculado com sucesso.",
        color: "success",
      }));
      await loadCommands(selectedModelId);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoadingCommandId(null);
    }
  };

  const checkboxTemplate = (item) => {
    
    const commandId = item.commandId ?? item.CommandId;
    const linkedRaw = item.linked ?? item.Linked;
    const linked = linkedRaw === "S" || linkedRaw === true || linkedRaw === 1;
    return (
      <input
        type="checkbox"
        checked={linked}
        disabled={loadingCommandId === commandId}
        onChange={() => handleCheckboxChange(item)}
      />
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Comandos de modelos de equipamentos</h5>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label className="form-label">Fabricante</label>
            <select
              className="form-select"
              value={selectedManufacturerId}
              onChange={handleManufacturerChange}
            >
              <option value="">Todos os fabricantes</option>
              {manufacturers.map((m) => {
                const id = m.manufacturerId ?? m.ManufacturerId;
                const name = m.manufacturerName ?? m.ManufacturerName ?? id;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Modelo de equipamento</label>
            <select
              className="form-select"
              value={selectedModelId}
              onChange={handleModelChange}
            >
              <option value="">Selecione o modelo</option>
              {filteredModels.map((m) => {
                const id = m.equipmentModelId ?? m.EquipmentModelId;
                const name = m.description ?? m.Description ?? m.equipmentModelName ?? id;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {isLoading && <Spinner />}

        {commands.length === 0 ? (
          <p className="text-muted mt-3">Nenhum comando encontrado.</p>
        ) : (
          <GridComponent
            dataSource={commands}
            allowPaging
            allowFiltering
            allowSorting
            filterSettings={{ type: "Menu" }}
            pageSettings={{ pageSize }}
            rowHeight={30}
            height="auto"
            locale="pt-BR"
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText=""
                width="10"
                textAlign="Center"
                template={checkboxTemplate}
                allowFiltering={false}
                allowSorting={false}
              />
              <ColumnDirective
                field="commandDescription"
                headerText="Descrição"
                width="250"
                textAlign="Left"
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Sort]} />
          </GridComponent>
        )}
      </div>
    </div>
  );
}
